import clsx from 'clsx';
import React from 'react';
import styles from '../RevenueVendorExpenseTable.module.scss';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import {
    infoToolTip,
    overlapInfoIconYellow,
    overlapInfoIconOrange,
    overlapInfoIconLightRed,
    overlapInfoIconDarkRed,
} from '../../../../../../assets/hostedassets';
import {
    CIRCULAR_TXN_KEY,
    NET_KEY,
    OVERLAP_TOOLTIP_HIDE_DELAY,
    OverlapRow,
    ROW_TOOLTIPS,
    TOTAL_COLUMN_KEY,
    circularSeverity,
    formatCircularPct,
    getNetValue,
    isOverlappingRow,
    isSummaryRow,
} from './overlap.constants';
import OverlapTooltipContent from './OverlapTooltipContent';
import IntraTooltipContent from './IntraTooltipContent';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_GST_OVERLAP_FLAG_CLICKED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';

// Row height (px) — matches .TableBodyRowItem height: 2.75rem. Used to stack the
// bottom-pinned summary rows.
const ROW_HEIGHT_PX = 44;

const SEVERITY_CLASS = {
    yellow: styles.CircularYellow,
    orange: styles.CircularOrange,
    lightRed: styles.CircularLightRed,
    darkRed: styles.CircularDarkRed,
};

// Per-severity hosted info icon — each PNG has the matching badge text colour baked in
// (a hosted <img> can't inherit currentColor). The dark-red PNG is #9e3b3b (correct on the
// white-bg summary text); on the filled dark pill a CSS filter whitens it
// (.CircularBadge.CircularDarkRed .OverlapInfoIcon)
const SEVERITY_ICON = {
    yellow: overlapInfoIconYellow,
    orange: overlapInfoIconOrange,
    lightRed: overlapInfoIconLightRed,
    darkRed: overlapInfoIconDarkRed,
};

// Severity-coloured % badge with an in-badge info icon. Used for Circular Txn.
// (per customer) and Intra Company Txn. (per month). `pill` = coloured pill vs plain coloured text.
// The info icon is the per-severity hosted PNG (SEVERITY_ICON), matching the badge colour.
function PctBadge({
    pct,
    tooltip,
    pill = true,
    rich = false,
    type,
}: {
    pct?: string;
    tooltip: React.ReactNode;
    pill?: boolean;
    rich?: boolean;
    type: 'circular' | 'intra';
}) {
    return (
        <span className={styles.CircularBadgeCell}>
            <span className={clsx(pill ? styles.CircularBadge : styles.CircularPctText, SEVERITY_CLASS[circularSeverity(pct)])}>
                {formatCircularPct(pct)}
                <Tooltip
                    content={tooltip}
                    direction="top"
                    usePortal
                    fitContent
                    hideArrow={rich}
                    hideDelay={OVERLAP_TOOLTIP_HIDE_DELAY}
                    contentClassName={rich ? styles.OverlapTooltipContent : undefined}
                >
                    <img
                        src={SEVERITY_ICON[circularSeverity(pct)]}
                        alt="info"
                        className={styles.OverlapInfoIcon}
                        onClick={() => Mixpanel.track(AICA_GST_OVERLAP_FLAG_CLICKED, { type })}
                    />
                </Tooltip>
            </span>
        </span>
    );
}

interface RowsProps {
    rows: any;
    headers: any;
    tableRef: any;
}

function Rows(props: RowsProps) {
    const { rows, headers } = props;
    // First summary row (Total B2B Revenue) gets a darker separator above it (per Figma).
    const firstSummaryIdx = Array.isArray(rows) ? rows.findIndex((r) => isSummaryRow(r)) : -1;
    return (
        <>
            {rows?.map?.((row, i) => {
                const overlapRow: OverlapRow = row;
                const summary = isSummaryRow(overlapRow);
                // Summary rows are pinned at the bottom; stack them by their distance from the last row.
                const rowCount = rows?.length || 0;
                const bottomOffset = summary ? (rowCount - 1 - i) * ROW_HEIGHT_PX : undefined;
                return (
                    <tr
                        key={i}
                        className={clsx(styles.TableBodyRow, {
                            [styles.SummaryRow]: summary,
                            [styles.IntraRow]: !!overlapRow?.__isIntra,
                            [styles.SummaryTop]: i === firstSummaryIdx,
                            [styles.NetB2bRow]: overlapRow?.name === 'Net B2B Revenue',
                        })}
                    >
                        {headers?.map((header, hIdx) => {
                            const key = header?.key;
                            const isCircular = key === CIRCULAR_TXN_KEY;
                            const isNet = key === NET_KEY;
                            const isName = hIdx === 0; // Customer Name (first) column
                            const value = row?.[key];

                            const cellClass = clsx(styles.TableBodyRowItem, {
                                [styles.RightAligned]:
                                    (typeof value === 'string' && value.includes('₹')) || (!isCircular && !isNet && !value),
                                [styles.TotalCol]: key === TOTAL_COLUMN_KEY,
                                [styles.CircularCol]: isCircular,
                                [styles.NetCol]: isNet,
                                [styles.StickyBottom]: summary,
                            });

                            let content: React.ReactNode;
                            if (isCircular) {
                                // Circular Txn. — % on overlapping rows. Pill for customers, coloured text for summary.
                                content = !isOverlappingRow(overlapRow) ? (
                                    '-'
                                ) : (
                                    <PctBadge
                                        pct={overlapRow.circularPct}
                                        tooltip={<OverlapTooltipContent row={overlapRow} />}
                                        pill={!summary}
                                        rich
                                        type="circular"
                                    />
                                );
                            } else if (isNet) {
                                // Net = Total − Circular (derived from circularTotal − circularValue).
                                content = getNetValue(overlapRow);
                            } else if (overlapRow.__isIntra && !isName && key !== TOTAL_COLUMN_KEY) {
                                // Intra Company Txn. row — per-month intra share % as a coloured pill.
                                content =
                                    value != null && value !== '-' && value !== '' ? (
                                        <PctBadge
                                            pct={String(value)}
                                            tooltip={
                                                <IntraTooltipContent
                                                    total={(overlapRow.__totalRow as Record<string, string> | undefined)?.[key]}
                                                    intraAmount={(overlapRow.__amounts as Record<string, number> | undefined)?.[key]}
                                                    pct={String(value)}
                                                />
                                            }
                                            pill
                                            rich
                                            type="intra"
                                        />
                                    ) : (
                                        '-'
                                    );
                            } else if (isName) {
                                const label = value || '-';
                                const rowTooltip = typeof value === 'string' ? ROW_TOOLTIPS[value] : undefined;
                                if (rowTooltip) {
                                    // Summary rows (Net B2B Revenue, Intra Company Txn.) — label + info icon.
                                    content = (
                                        <span className={styles.NameWithInfo}>
                                            {label}
                                            <Tooltip content={rowTooltip} direction="top" usePortal fitContent>
                                                <img src={infoToolTip} alt="info" className={styles.RowInfoIcon} />
                                            </Tooltip>
                                        </span>
                                    );
                                } else if (!summary && value && value !== '-') {
                                    // Customer Name — clamp to 2 lines + ellipsis; full name on hover (per Figma).
                                    content = (
                                        <Tooltip content={`${value}`} direction="top" fitContent usePortal>
                                            <span className={styles.CustomerName}>{label}</span>
                                        </Tooltip>
                                    );
                                } else {
                                    content = <span className={styles.CustomerName}>{label}</span>;
                                }
                            } else if (key === 'state' && value !== '-' && value?.length > 25) {
                                content = (
                                    <Tooltip content={`${value}`} direction="bottom" fitContent>
                                        <span style={{ whiteSpace: 'nowrap' }}>{value.substring(0, 25)}...</span>
                                    </Tooltip>
                                );
                            } else {
                                content = <>{value || '-'}</>;
                            }

                            return (
                                <td
                                    className={cellClass}
                                    key={`${header?.name}-${i}`}
                                    style={summary ? { bottom: `${bottomOffset}px` } : undefined}
                                >
                                    {content}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </>
    );
}

export default Rows;
